import { Injectable, inject } from '@angular/core';
import { map, catchError } from 'rxjs/operators';
import { Observable } from 'rxjs/';
import { KanziControlErrorsService } from '../control-errors/kanzi-control-errors.service';
import { HttpClient } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class DataXmlFormaterService {
  private readonly http = inject(HttpClient);
  private readonly errorService = inject(KanziControlErrorsService);

  /**
   * Fetches a CSV/XML file from the given URL and parses it into a structured list.
   * Appends a timestamp query parameter to prevent browser caching.
   * Properly handles URLs that already contain query parameters (e.g., presigned S3 URLs).
   * @param url - The URL of the CSV/XML file to fetch
   * @param separator - The column delimiter used in the file (defaults to ';')
   * @returns Observable emitting an object with a count and the parsed results array
   */
  getDataFromXml(url: string, separator: string = ';'): Observable<any> {
    const separator_char = url.includes('?') ? '&' : '?';
    const urlWithTimestamp = `${url}${separator_char}v=${new Date().getTime()}`;
    return this.http.get(urlWithTimestamp, { responseType: 'text' }).pipe(
      map((data) => this.extractData(data, separator)),
      map((results) => ({
        count: results ? results['length'] : 0,
        results: results,
      })),
      catchError(this.errorService.handleError('Error al Leer Archivo'))
    );
  }

  /**
   * Parses raw CSV/XML text into an array of objects keyed by the header row.
   * Numeric values are automatically coerced unless they exceed 20 characters.
   * @param text - The raw text content of the CSV/XML file
   * @param separator - The column delimiter used in the file (defaults to ';')
   * @returns Array of parsed row objects with header fields as keys
   */
  private extractData(text: string, separator: string = ';'): any[] {
    const csvData = text || '';
    const allTextLines: string[] = csvData.split(/\r\n|\n/);
    allTextLines.pop();
    const headers = allTextLines ? allTextLines[0].split(separator) : [];
    allTextLines.shift();
    const lines = Array.from(allTextLines, (item) => {
      const data = item.split(separator);
      return headers.reduce(function (acc: any, actual, i) {
        acc[actual] = isNaN(Number(data[i]))
          ? data[i]
          : data[i].length > 20
          ? data[i]
          : Number(data[i]);
        return acc;
      }, {});
    });
    return lines;
  }
}
